import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { SCHOLARSHIP_STATES, type Page, type ScholarshipApplication, type ScholarshipCriteria } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

/**
 * ANHRD scholarship review + scoring
 * (packages/modules/m28-education/src/routes/scholarships.ts). The academic
 * score auto-fills from the candidate's own released exam results at
 * application time — this console only ever enters the criteria score
 * (interview / means-test / other) and records the final decision, plus
 * optional bond terms the applicant then accepts or declines themselves.
 */
export function Scholarships() {
  const { persona } = useAuth();
  const isBoard = persona?.roles.includes('ANHRD_BOARD') ?? false;
  const [tab, setTab] = useState<'applications' | 'criteria'>('applications');

  return (
    <div>
      <PageHeader title="Scholarships (ANHRD)" subtitle="Agency for National Human Resource Development scholarship pipeline." />
      <Tabs tabs={[{ key: 'applications', label: 'Applications' }, { key: 'criteria', label: 'Scoring Criteria' }]} value={tab} onChange={setTab} />
      {!isBoard && tab === 'applications' ? (
        <div className="mb-4">
          <ErrorNote message="Listing and deciding scholarship applications requires the ANHRD Board role." />
        </div>
      ) : null}
      {tab === 'applications' ? <ApplicationsTab isBoard={isBoard} /> : <CriteriaTab />}
    </div>
  );
}

function CriteriaTab() {
  const criteriaQ = useApiQuery<ScholarshipCriteria>('/api/v1/m28/scholarships/criteria');
  if (criteriaQ.loading) return <Spinner />;
  if (criteriaQ.error) return <ErrorNote message={criteriaQ.error} />;
  const c = criteriaQ.data;
  if (!c) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card p-5">
        <h2 className="mb-1 text-sm font-bold text-slate-700">Academic score (max {c.academic.maxScore})</h2>
        <p className="mb-3 text-xs text-slate-500">{c.academic.note}</p>
        <div className="grid grid-cols-3 gap-1.5 text-xs">
          {Object.entries(c.academic.gradePoints).map(([grade, points]) => (
            <div key={grade} className="flex justify-between rounded-lg border border-slate-200 px-2 py-1">
              <span className="font-semibold">{grade}</span>
              <span className="tabular-nums text-slate-500">{points} pts</span>
            </div>
          ))}
        </div>
      </div>
      <div className="card p-5">
        <h2 className="mb-1 text-sm font-bold text-slate-700">Criteria score (max {c.criteria.maxScore})</h2>
        <p className="text-xs text-slate-500">{c.criteria.note}</p>
        <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-slate-400">Max total score: {c.maxTotalScore}</p>
      </div>
    </div>
  );
}

function ApplicationsTab({ isBoard }: { isBoard: boolean }) {
  const [status, setStatus] = useState('');
  const [programme, setProgramme] = useState('');
  const [q, setQ] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  if (programme) qs.set('programme', programme);
  if (q) qs.set('q', q);
  const listQ = useApiQuery<Page<ScholarshipApplication>>(isBoard ? `/api/v1/m28/scholarships?${qs.toString()}` : null);

  if (!isBoard) return null;

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        <input className="field-input !w-52" placeholder="Search applicant NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
        <input className="field-input !w-52" placeholder="Programme…" value={programme} onChange={(e) => setProgramme(e.target.value)} />
        <select className="field-input !w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All states</option>
          {SCHOLARSHIP_STATES.map((s) => (
            <option key={s} value={s}>
              {s.replaceAll('_', ' ')}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No scholarship applications match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Applicant</th>
                <th className="table-head">Programme</th>
                <th className="table-head">Institution</th>
                <th className="table-head text-right">Academic</th>
                <th className="table-head text-right">Criteria</th>
                <th className="table-head text-right">Total</th>
                <th className="table-head">State</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(s.id)}>
                  <td className="table-cell font-mono text-xs">{s.applicantNin}</td>
                  <td className="table-cell font-medium">{s.programme}</td>
                  <td className="table-cell text-xs text-slate-500">{s.institution}</td>
                  <td className="table-cell text-right tabular-nums">{s.academicScore ?? '—'}</td>
                  <td className="table-cell text-right tabular-nums">{s.criteriaScore ?? '—'}</td>
                  <td className="table-cell text-right font-semibold tabular-nums">{s.totalScore ?? '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId ? <ApplicationDetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} /> : null}
    </div>
  );
}

function ApplicationDetailModal({ id, onClose, onChanged }: { id: string; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<ScholarshipApplication>(`/api/v1/m28/scholarships/${id}`);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [criteriaScore, setCriteriaScore] = useState('');
  const [notes, setNotes] = useState('');
  const [bondYears, setBondYears] = useState('4');
  const [bondAmount, setBondAmount] = useState('');
  const [showDecision, setShowDecision] = useState<'approve' | 'reject' | null>(null);

  const app = detailQ.data;

  const act = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m28/scholarships/${id}${path}`, body);
      detailQ.reload();
      onChanged();
      setShowDecision(null);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Scholarship application" onClose={onClose} wide>
      {detailQ.loading || !app ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex items-center gap-2">
            <StatusBadge status={app.state} />
            <span className="font-mono text-xs text-slate-500">{app.applicantNin}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Programme</dt>
              <dd className="font-medium">{app.programme}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Institution</dt>
              <dd className="font-medium">{app.institution}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Academic score</dt>
              <dd className="font-medium tabular-nums">{app.academicScore ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Criteria score</dt>
              <dd className="font-medium tabular-nums">{app.criteriaScore ?? '—'}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">Total score</dt>
              <dd className="font-bold tabular-nums">{app.totalScore ?? '—'}</dd>
            </div>
            {app.rank ? (
              <div className="flex justify-between">
                <dt className="text-slate-500">Rank</dt>
                <dd className="font-bold tabular-nums">#{app.rank}</dd>
              </div>
            ) : null}
          </dl>
          {app.decisionNotes ? <p className="text-xs text-slate-500">Decision notes: {app.decisionNotes}</p> : null}
          {app.bondYears ? (
            <p className="text-xs text-slate-500">
              Bond: {app.bondYears} years, {scr(app.bondAmount)}
            </p>
          ) : null}

          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 pt-3">
            {app.state === 'ACADEMIC_SCORED' ? (
              <button className="btn-primary !px-2.5 !py-1 text-xs" disabled={busy} onClick={() => act('/review')}>
                Move to review
              </button>
            ) : null}
            {app.state === 'UNDER_REVIEW' ? (
              <div className="flex items-end gap-2">
                <Field label="Criteria score" className="!mb-0">
                  <input
                    className="field-input !w-28 !py-1 text-xs"
                    type="number"
                    min="0"
                    value={criteriaScore}
                    onChange={(e) => setCriteriaScore(e.target.value)}
                  />
                </Field>
                <button
                  className="btn-primary !px-2.5 !py-1 text-xs"
                  disabled={busy || criteriaScore === ''}
                  onClick={() => act('/score', { criteriaScore: Number.parseInt(criteriaScore, 10) })}
                >
                  Submit score
                </button>
              </div>
            ) : null}
            {app.state === 'SCORED' && showDecision === null ? (
              <>
                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => setShowDecision('approve')}>
                  Approve
                </button>
                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => setShowDecision('reject')}>
                  Reject
                </button>
              </>
            ) : null}
            {app.state === 'APPROVED' ? <p className="text-xs text-slate-400">Awaiting the applicant to accept or decline the bond terms.</p> : null}
          </div>

          {showDecision ? (
            <div className="space-y-2 rounded-lg border border-slate-200 p-3">
              <Field label="Decision notes">
                <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </Field>
              {showDecision === 'approve' ? (
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Bond years">
                    <input className="field-input" type="number" min="1" value={bondYears} onChange={(e) => setBondYears(e.target.value)} />
                  </Field>
                  <Field label="Bond amount (SCR)">
                    <input className="field-input" type="number" min="0" step="0.01" value={bondAmount} onChange={(e) => setBondAmount(e.target.value)} />
                  </Field>
                </div>
              ) : null}
              <div className="flex justify-end gap-2">
                <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setShowDecision(null)}>
                  Cancel
                </button>
                <button
                  className={showDecision === 'approve' ? 'btn-success !px-2.5 !py-1 text-xs' : 'btn-danger !px-2.5 !py-1 text-xs'}
                  disabled={
                    busy ||
                    notes.trim().length < 3 ||
                    (showDecision === 'approve' && (!bondYears || !bondAmount))
                  }
                  onClick={() =>
                    act('/decision', {
                      approve: showDecision === 'approve',
                      notes,
                      ...(showDecision === 'approve'
                        ? { bondYears: Number.parseInt(bondYears, 10), bondAmount: Number.parseFloat(bondAmount) }
                        : {}),
                    })
                  }
                >
                  Confirm {showDecision === 'approve' ? 'approval' : 'rejection'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
